import { useState, useEffect } from 'react';
import { CheckCircle, ArrowLeft, AlertCircle } from 'lucide-react';
import ThemeToggle from '../ThemeToggle';

const API = import.meta.env.VITE_API_URL;

interface Props { navigate: (path: string) => void }

export default function Apply({ navigate }: Props) {
  const [form, setForm] = useState<any>({
    display_name: '',
    legal_name: '',
    business_name: '',
    email: '',
    phone: '',
    address_line1: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US',
    payment_method_preference: 'check',
    referred_by_referral_code: '',
    why_affiliate: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const setField = (k: string, v: string) => setForm({ ...form, [k]: v });

  // Pre-fill the referring affiliate's code from a ?ref= link
  // (e.g. https://affiliate.oasisautokonnect.com/apply?ref=ABC123).
  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get('ref');
    if (ref) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setForm((f: any) => ({ ...f, referred_by_referral_code: ref.trim().toUpperCase() }));
    }
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.display_name || !form.email) {
      setError('Display name and email are required.');
      return;
    }
    setLoading(true);
    const res = await fetch(`${API}/public/affiliate-applications`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    });
    const json = (await res.json()) as { success: boolean; error?: string };
    setLoading(false);
    if (!json.success) {
      setError(json.error || 'Submission failed. Please try again.');
      return;
    }
    setSuccess(true);
  };

  if (success) {
    return (
      <div className="min-h-screen bg-white dark:bg-slate-950 font-inter flex items-center justify-center p-4">
        <div className="max-w-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-sm p-8 text-center">
          <CheckCircle className="w-16 h-16 text-green-600 dark:text-green-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Application Received</h2>
          <p className="text-slate-600 dark:text-slate-300 mb-6">
            Thank you for applying to the OAK affiliate program. We've sent a confirmation email to <strong>{form.email}</strong> and will review your application shortly.
          </p>
          <button onClick={() => navigate('/')} className="px-6 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg">
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 font-inter">
      <div className="border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-xl font-bold">
            <span className="text-cyan-600 dark:text-cyan-400">OAK</span>
            <span className="text-slate-700 dark:text-slate-300 text-base">Affiliates</span>
          </button>
          <ThemeToggle className="text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800" />
        </div>
      </div>

      <div className="max-w-3xl mx-auto py-8 px-4">
        <button onClick={() => navigate('/')} className="flex items-center gap-2 text-cyan-600 dark:text-cyan-400 hover:underline mb-4 text-sm">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </button>

        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-sm p-8">
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">Affiliate Application</h1>
          <p className="text-slate-600 dark:text-slate-300 mb-6">
            Tell us about yourself. After review, you'll receive an email with next steps. Fields marked * are required.
          </p>

          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded text-red-700 dark:text-red-300 text-sm flex items-start gap-2">
              <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={submit} className="space-y-4">
            <fieldset className="border border-slate-200 dark:border-slate-700 rounded-lg p-4">
              <legend className="text-sm font-semibold text-slate-700 dark:text-slate-200 px-2">Contact</legend>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Display Name *">
                  <input type="text" value={form.display_name} onChange={(e) => setField('display_name', e.target.value)} className="form-input" required />
                </Field>
                <Field label="Email *">
                  <input type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} className="form-input" required />
                </Field>
                <Field label="Phone">
                  <input type="tel" value={form.phone} onChange={(e) => setField('phone', e.target.value)} className="form-input" />
                </Field>
                <Field label="Referral Code (if any other affiliate referred you)">
                  <input type="text" value={form.referred_by_referral_code} onChange={(e) => setField('referred_by_referral_code', e.target.value.toUpperCase())} className="form-input" placeholder="e.g., ABC123" />
                </Field>
              </div>
            </fieldset>

            <fieldset className="border border-slate-200 dark:border-slate-700 rounded-lg p-4">
              <legend className="text-sm font-semibold text-slate-700 dark:text-slate-200 px-2">W-9 Details (we'll need these before paying commissions)</legend>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Legal Name (as on tax return)">
                  <input type="text" value={form.legal_name} onChange={(e) => setField('legal_name', e.target.value)} className="form-input" />
                </Field>
                <Field label="Business Name (if applicable)">
                  <input type="text" value={form.business_name} onChange={(e) => setField('business_name', e.target.value)} className="form-input" />
                </Field>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <Field label="Address">
                  <input type="text" value={form.address_line1} onChange={(e) => setField('address_line1', e.target.value)} className="form-input" />
                </Field>
                <Field label="City">
                  <input type="text" value={form.city} onChange={(e) => setField('city', e.target.value)} className="form-input" />
                </Field>
                <Field label="State">
                  <input type="text" value={form.state} onChange={(e) => setField('state', e.target.value)} className="form-input" />
                </Field>
                <Field label="ZIP">
                  <input type="text" value={form.postal_code} onChange={(e) => setField('postal_code', e.target.value)} className="form-input" />
                </Field>
              </div>
              <p className="text-xs text-gray-500 mt-3">
                Your TIN (SSN/EIN) and banking info will be collected securely in the portal after your application is approved. Don't include them here.
              </p>
            </fieldset>

            <fieldset className="border border-slate-200 dark:border-slate-700 rounded-lg p-4">
              <legend className="text-sm font-semibold text-slate-700 dark:text-slate-200 px-2">Payment Preference</legend>
              <Field label="How would you prefer to receive commissions?">
                <select value={form.payment_method_preference} onChange={(e) => setField('payment_method_preference', e.target.value)} className="form-input">
                  <option value="check">Paper check</option>
                  <option value="ach_manual">ACH bank transfer</option>
                  <option value="wallet_credit">Wallet credit (if I'm also a customer)</option>
                </select>
              </Field>
            </fieldset>

            <fieldset className="border border-slate-200 dark:border-slate-700 rounded-lg p-4">
              <legend className="text-sm font-semibold text-slate-700 dark:text-slate-200 px-2">About You (optional)</legend>
              <Field label="Why do you want to be an OAK affiliate? Anything we should know about how you'll refer customers?">
                <textarea value={form.why_affiliate} onChange={(e) => setField('why_affiliate', e.target.value)} rows={4} className="form-input" />
              </Field>
            </fieldset>

            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => navigate('/')} className="px-6 py-3 text-slate-700 dark:text-slate-300">Cancel</button>
              <button type="submit" disabled={loading} className="flex-1 px-6 py-3 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg font-medium disabled:opacity-50">
                {loading ? 'Submitting...' : 'Submit Application'}
              </button>
            </div>
          </form>
        </div>
      </div>

      <style>{`
        .form-input { width: 100%; border: 1px solid #cbd5e1; border-radius: 6px; padding: 8px 12px; font-size: 14px; background: white; color: #0f172a; }
        .form-input:focus { outline: none; border-color: #0891b2; box-shadow: 0 0 0 2px rgb(8 145 178 / 0.2); }
        .dark .form-input { background: #1e293b; border-color: #475569; color: #f1f5f9; }
        .dark .form-input:focus { border-color: #22d3ee; }
      `}</style>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm text-slate-700 dark:text-slate-300 mb-1">{label}</label>
      {children}
    </div>
  );
}
